import { inject, InjectionToken, Service, signal } from '@angular/core';
import { environment } from '@environements';
import { filter, map, merge, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { NotificationService } from './notification.service';
import { RealtimeEvent } from './interfaces/realtime.interface';

/** How long the socket may stay down before the UI says live updates are paused. */
export const PAUSED_GRACE_MS = 5_000;
/** How long the "caught up" confirmation stays after a long outage. */
export const CAUGHT_UP_MS = 3_000;
/** A tab hidden for longer than this re-reads over REST when it comes back. */
export const HIDDEN_RESYNC_MS = 15_000;
/** How the socket is created; tests swap it for a fake instead of mocking the package. */
export const REALTIME_SOCKET_FACTORY = new InjectionToken<typeof io>('REALTIME_SOCKET_FACTORY', {
  providedIn: 'root',
  factory: () => io,
});

/** A connection that lasted at least this long was healthy; shorter ones count as churn. */
export const STABLE_CONNECTION_MS = 30_000;
const RETRY_START_MS = 2_000;
const RETRY_MAX_MS = 30_000;

/**
 * One live connection per tab to the server's push channel. Events are only a
 * signal that something changed: whoever listens re-reads over REST. A missed
 * event is therefore harmless, as long as something re-reads after any gap,
 * which is what `resync$` is for.
 */
@Service()
export class RealtimeService {
  private notifications = inject(NotificationService);
  private createSocket = inject(REALTIME_SOCKET_FACTORY);

  private socket: Socket | null = null;
  private graceTimer: ReturnType<typeof setTimeout> | undefined;
  private caughtUpTimer: ReturnType<typeof setTimeout> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private retryDelay = RETRY_START_MS;
  private hiddenAt: number | null = null;
  private connectedAt: number | null = null;

  private _paused = signal(false);
  private _caughtUp = signal(false);
  private _events = new Subject<RealtimeEvent>();
  private _resync = new Subject<void>();

  /** True once the connection has been down longer than the grace period. */
  readonly paused = this._paused.asReadonly();
  /** True for a few seconds after reconnecting from a visible `paused` state. */
  readonly caughtUp = this._caughtUp.asReadonly();
  readonly events$: Observable<RealtimeEvent> = this._events.asObservable();
  /** Fires when events may have been missed (every connect, or a long-hidden tab came back): re-read over REST. */
  readonly resync$: Observable<void> = this._resync.asObservable();
  /**
   * Everything a notification list or badge reacts to: a push about notifications
   * (carrying the event), or a resync (carrying nothing, meaning "re-read it all").
   */
  readonly notificationsChanged$: Observable<RealtimeEvent | undefined> = merge(
    this.events$.pipe(filter((event) => event.channel === 'notifications')),
    this.resync$.pipe(map((): undefined => undefined)),
  );

  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now();
      return;
    }
    const hiddenFor = this.hiddenAt === null ? 0 : Date.now() - this.hiddenAt;
    this.hiddenAt = null;
    if (hiddenFor >= HIDDEN_RESYNC_MS) {
      this._resync.next();
    }
  };

  /** Opens the connection. Safe to call twice. */
  start(): void {
    if (this.socket) {
      return;
    }
    const socket = this.createSocket(environment.REALTIME_URL, {
      path: environment.REALTIME_PATH,
      // The server speaks websocket only: long-polling would need sticky sessions.
      transports: ['websocket'],
      // The signed session cookie is what authenticates the handshake.
      withCredentials: true,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: RETRY_MAX_MS,
    });
    this.socket = socket;

    socket.on('connect', () => this.onConnect());
    socket.on('disconnect', (reason: string) => this.onDisconnect(reason));
    socket.on('connect_error', () => this.onConnectError());
    socket.on('event', (event: RealtimeEvent) => this._events.next(event));

    document.addEventListener('visibilitychange', this.onVisibility);
  }

  stop(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    clearTimeout(this.graceTimer);
    this.graceTimer = undefined;
    clearTimeout(this.caughtUpTimer);
    clearTimeout(this.retryTimer);
    this.socket?.disconnect();
    this.socket = null;
    this.retryDelay = RETRY_START_MS;
    this.connectedAt = null;
    this.hiddenAt = null;
    this._paused.set(false);
    this._caughtUp.set(false);
  }

  private onConnect(): void {
    clearTimeout(this.graceTimer);
    this.graceTimer = undefined;
    clearTimeout(this.retryTimer);
    // The wait is not reset here: a connection that the server drops again at once
    // (evicted past the per-user cap) must keep backing off, not start over.
    this.connectedAt = Date.now();
    const wasPaused = this._paused();
    this._paused.set(false);
    if (wasPaused) {
      this._caughtUp.set(true);
      clearTimeout(this.caughtUpTimer);
      this.caughtUpTimer = setTimeout(() => this._caughtUp.set(false), CAUGHT_UP_MS);
    }
    // Anything published before this socket was in its rooms (the very first connect
    // included) or while it was down is only in the database: read it from there.
    this._resync.next();
  }

  private onConnectError(): void {
    this.startGrace();
    // A network failure is retried by socket.io itself. A refusal by the server's
    // handshake check ends the socket for good, so retry that here, backing off:
    // a wrong origin or a missing cookie must not become a tight loop.
    if (this.socket && !this.socket.active) {
      this.scheduleReconnect();
    }
  }

  private onDisconnect(reason: string): void {
    const lasted = this.connectedAt === null ? 0 : Date.now() - this.connectedAt;
    this.connectedAt = null;
    if (reason === 'io client disconnect') {
      return;
    }
    const stable = lasted >= STABLE_CONNECTION_MS;
    if (stable) {
      this.retryDelay = RETRY_START_MS;
    }
    this.startGrace();
    // The server ended this socket (token expired, session ended, over the cap):
    // socket.io does not retry those on its own, so do it once the session is confirmed.
    // After a healthy connection that is immediate (token expiry); after one that
    // barely lasted (evicted over the cap) it backs off, so tabs past the limit
    // slow down instead of knocking each other off in turn.
    if (reason === 'io server disconnect') {
      if (stable) {
        this.reconnectWithSession();
      } else {
        this.scheduleReconnect();
      }
    }
  }

  private startGrace(): void {
    if (this.graceTimer !== undefined || this._paused()) {
      return;
    }
    this.graceTimer = setTimeout(() => {
      this.graceTimer = undefined;
      this._paused.set(true);
    }, PAUSED_GRACE_MS);
  }

  /**
   * Any authenticated request refreshes an expired session through the HTTP
   * interceptor (or sends the user to login if it is gone), so asking for the
   * unread count first means the new handshake carries a valid cookie.
   */
  private reconnectWithSession(): void {
    clearTimeout(this.retryTimer);
    this.notifications.getUnreadCount().subscribe({
      next: () => this.socket?.connect(),
      error: (error: unknown) => {
        // 401 is handled by the HTTP interceptor (it signs the user out); 403 means the
        // account is no longer allowed in. Neither will get better by asking again.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) {
          return;
        }
        this.scheduleReconnect();
      },
    });
  }

  private scheduleReconnect(): void {
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.reconnectWithSession(), this.retryDelay);
    this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX_MS);
  }
}
